/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import Script from 'react-load-script';
import {connect} from 'react-redux';
import { getActiveProject } from '../reducers/mainReducer';
import './forgeView.css';
import Message from './message';
import repo from '../Repository';
import { viewerCss, viewerJs } from './shared';

let Autodesk = null;

export class ForgeView extends Component {

    constructor(props){
      super(props);

      this.viewerDiv = React.createRef();
      this.viewer = null;
      this.state = { modelLoaded: false };
    }

    componentDidMount() {
        // If the viewer script was already loaded from a previous mount (e.g.,
        // Configure step), re-initialize directly since react-load-script
        // won't fire onLoad for a script already in the DOM.
        if (window.Autodesk && !this.viewer) {
            this.handleScriptLoad();
        }
    }

    handleScriptLoad() {
        if (this.viewer) return; // Already initialized

        const options = repo.hasAccessCode() ?
                            { accessToken: repo.getAccessCode() } :
                            { env: 'Local' };

        Autodesk = window.Autodesk;

        const container = this.viewerDiv.current;
        this.viewer = new Autodesk.Viewing.GuiViewer3D(container);

        // uncomment this for Viewer debugging
        //this.viewer.debugEvents(true);

        Autodesk.Viewing.Initializer(options, this.handleViewerInit.bind(this));
    }

    handleViewerInit() {
        const errorCode = this.viewer.start();
        if (errorCode)
            return;

        // Hide Autodesk progress bar — use ConfigSelect progress instead
        if (this.viewer.setProgressBarVisible) {
            this.viewer.setProgressBarVisible(false);
        }

        // Set up autocam callback to prevent "not a function" errors
        if (this.viewer.autocam && !this.viewer.autocam.pivotDisplayCallback) {
            this.viewer.autocam.pivotDisplayCallback = () => {};
        }

        // orient camera in the same way as it's on the thumbnail
        // corresponding to ViewOrientationTypeEnum.kIsoTopRightViewOrientation
        const viewer = this.viewer;
        this.viewer.addEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, (event) => {

            const viewCubeExtensionId = "Autodesk.ViewCubeUi";

            // this is not perfect, because the view transition is visible, so it's a place to improve someday
            if (event.extensionId === viewCubeExtensionId) {

                const viewCubeUI = event.target.getExtension(viewCubeExtensionId);
                viewCubeUI.setViewCube("front top right");

                viewer.removeEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT);
            }
        });

        // skip loading of svf when there is no active project svf
        if (!this.props.activeProject.svf)
            return;

        Autodesk.Viewing.Document.load(
            this.getSvfUrl(), this.onDocumentLoadSuccess.bind(this), () => {}
        );
    }

    componentDidUpdate(prevProps) {
        if (Autodesk && (this.props.activeProject.svf !== prevProps.activeProject.svf)) {
            this.setState({ modelLoaded: false });
            Autodesk.Viewing.Document.load(
                this.getSvfUrl(), this.onDocumentLoadSuccess.bind(this), () => {}
            );
        }
    }

    componentWillUnmount() {
        if (this.viewer) {
            try {
                // Disable autocam before cleanup to prevent callback errors
                if (this.viewer.autocam) {
                    this.viewer.autocam.pivotDisplayCallback = null;
                }
                this.viewer.finish();
            } catch (e) {
                // Ignore viewer cleanup errors - these are internal Autodesk issues
                console.warn('Viewer cleanup warning:', e.message);
            }
            this.viewer = null;
            try {
                Autodesk.Viewing.shutdown();
            } catch (e) {
                console.warn('Viewer shutdown warning:', e.message);
            }
        }
    }

    getSvfUrl() {
        return this.props.activeProject.svf + `/bubble.json`;
    }

    onDocumentLoadSuccess(viewerDocument) {
        // Guard against callback firing after component unmount
        if (!this.viewer) return;

        const defaultModel = viewerDocument.getRoot().getDefaultGeometry();
        this.viewer.loadDocumentNode(viewerDocument, defaultModel);
        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, () => {
            this.setState({ modelLoaded: true });
        });
    }

    render() {
        return (
            <div className="modelContainer fullheight">
                <Message/>
                <div className="viewer" id="AutodeskViewer">
                    <div className={`viewer-splash ${this.state.modelLoaded ? 'hidden' : ''}`}>
                        <div className="viewer-splash-text">
                            Config<span>Select</span>.com
                        </div>
                        <div className="viewer-splash-sub">Loading your model...</div>
                        <div className="viewer-splash-loader"></div>
                    </div>
                    <div ref={this.viewerDiv}></div>
                    <link rel="stylesheet" type="text/css" href={ viewerCss } />
                    <Script url={ viewerJs } onLoad={this.handleScriptLoad.bind(this)} />
                </div>
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function (store){
    return {
      activeProject: getActiveProject(store)
    };
  })(ForgeView);